<template>
  <div>
    <molecules-hero-header
      class="max-w-[72rem] mx-auto !h-auto !min-h-[auto] pt-10 md:pt-16 pb-[4.125rem] md:pb[4.5rem] !bg-none !text-left md:px-4 xl:px-4"
      :image-config="{
        img: calculatorData.hero.img,
        class: 'max-w-full w-[28rem] lg:h-[23.75rem]',
        dimensions: {
          width: 448,
          height: 380
        }
      }"
      title-class="pt-8 md:pt-0"
      sub-title-class="mt-5 md:mt-8"
      :buttons="calculatorData.hero.buttons"
      title-tag="p"
      hide-bg-on-mobile
    >
      <template
        v-if="calculatorData.hero.header"
        #header
      >
        <!-- eslint-disable-next-line vue/no-v-html -->
        <p v-html="calculatorData.hero.header"></p>
      </template>

      <template
        v-if="calculatorData.hero.subHeader"
        #sub-header
      >
        <!-- eslint-disable-next-line vue/no-v-html -->
        <p v-html="calculatorData.hero.subHeader"></p>
      </template>
    </molecules-hero-header>

    <MoleculesCalculatorHomeLoan
      :header="calculatorData.calculator.header"
      :sub-header="calculatorData.calculator.subHeader"
      :disclaimer="calculatorData.calculator.disclaimer"
      :calculator="type"
    />

    <molecules-home-great-deal
      :cards="GREAT_DEAL_ITEMS"
      :col-count="4"
      class="!bg-blue-5"
      card-wrapper-max-width="w-wrapper-lg"
      image-lazy-load
    >
      <template #header>
        Our <strong class="font-extrabold">experts</strong> make comparing <strong class="font-extrabold">home loans
          easy.</strong>
      </template>

      <template #sub-header>
        Our four simple steps can help you save.
      </template>
    </molecules-home-great-deal>

    <div class="bg-white px-4 lg:px-0 pt-[2.125rem] pb-20 lg:pt-20">
      <div class="max-w-wrapper-md mx-auto">
        <p class="text-2xl  md:text-4.5xl text-center leading-10 md:leading-[3rem]">
          How
          <strong class="font-extrabold">our experts</strong> have<br />
          <strong class="font-extrabold">helped Australians</strong>
          like you
        </p>

        <V2MoleculesReviews
          layout="reviews"
          :tags="vertical.replace(/\s/g, '-')"
        />
      </div>
    </div>

    <molecules-home-footer-info theme="gray-boxed">
      <template #title>
        Things you should know
      </template>

      <template #description>
        <p class="mb-6">
          Here are a few essential bits of information about us. Your home loan is all about you, but this should give you confidence you're dealing with professionals. It's also legally important for us to tell you this.
        </p>

        <p class="mb-6">
          Compare Club is an Authorised Corporate Credit Representative of Alternative Media Pty Ltd ABN 17 149 089 716 (Australian Credit Licence Number 486326).
        </p>

        <p class="mb-6">
          Everything on this page only provides general information about home loans. It's been prepared without taking into account your objectives, financial situation or needs. We recommend that you consider whether it is appropriate for your circumstances. Your full financial situation will need to be reviewed prior to acceptance of any offer or product. It does not constitute legal, tax or financial advice - always seek professional advice in relation to your individual circumstances. Products and rates quoted are subject to lenders terms and conditions, fees and charges and eligibility criteria apply.
        </p>

        <p class="mb-6">
          * The Interest Rate quoted is for a variable rate owner occupied loan with principal and interest payments, loan to value ratio below 60% and minimum loan amount of $250,000. Interest Rates are correct as at {{ homeLoanRates.cashRateDate }} and subject to change at any time. The comparison rate is based on a loan amount of $150,000, over a 25 year term. Warning: This comparison rate is true only for the example given and may not include all fees and charges. Different terms, fees or other loan amounts might result in a different comparison rate. Terms, conditions, fees and charges apply and your full financial situation would need to be reviewed prior to acceptance of any offer or product.
        </p>
      </template>
    </molecules-home-footer-info>
  </div>
</template>

<script setup>
import { VERTICAL } from '~/helpers/vertical.constants'
import HomeLoanService from '~/services/home-loan'

import {
  GREAT_DEAL_ITEMS
} from '~/constants/pages/home-loans'
import { CALCULATORS, CALCULATOR_DISCLAIMER } from '~/constants/homeloans.constant'

const service = new HomeLoanService()

const trustpilotStore = useTrustpilotStore()

const vertical = VERTICAL.HOME_LOANS
const verticalData = VERTICALS[vertical]
const $route = useRoute()
const type = strToCamelCase($route.params.type)
const calculatorTypes = CALCULATORS.map(e => strToKebabCase(e))

if (!calculatorTypes.includes($route.params.type)) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Page not found',
    fatal: true
  })
}

defineOptions({
  name: 'HomeLoansCalculators'
})

const calculatorData = computed(() => {
  const heroImg = img => `${APP_ASSET_BASEURL}edge/static/images/products/home-loans/calculators/${img}.svg`

  const disclaimer = getKey(type, CALCULATOR_DISCLAIMER)

  return getKey(type, {
    refinance: {
      hero: {
        img: heroImg('refinance-calculator-hero'),
        header: `
              <strong class="font-extrabold">Refinance Calculator</strong><br>
              Get started
            `,
        subHeader: 'Use our calculator to see how much you could save just by<br class="hidden lg:block" /> switching to another lender on our panel.',
        buttons: [
          {
            theme: 'primary',
            text: 'Get started',
            url: '#calculator'
          }
        ]
      },

      calculator: {
        header: '<strong class="font-extrabold">Refinance</strong> and <strong class="font-extrabold">Save</strong>',
        subHeader: `
            Calculate just how much interest you could be saving when you refinance your home loan with one of the 40+ lenders on our panel today.
            `,
        disclaimer
      }
    },

    borrowingPower: {
      hero: {
        img: heroImg('borrowing-power-hero'),
        header: `
              <strong class="font-extrabold">Borrowing power<br/>
              calculator</strong>
            `,
        subHeader: 'Use our borrowing power calculator to get an estimate for how<br class="hidden lg:block"/> much you can borrow for your home loan in just minutes.',
        buttons: [
          {
            theme: 'primary',
            text: 'Get started',
            url: '#calculator'
          }
        ]
      },

      calculator: {
        header: 'Discover your <strong class="font-extrabold">borrowing power</strong>',
        subHeader: 'Use our borrowing power calculator to get a quick estimate on how much you could borrow based on your current<br class="hidden lg:block"/> income and your current financial commitments.',
        disclaimer
      }
    },

    stampDuty: {
      hero: {
        img: heroImg('stamp-duty-hero'),
        header: '<strong class="font-extrabold">Stamp duty calculator</strong>',
        subHeader: 'Use our stamp duty calculator to see how much stamp duty (land transfer) and government fees you need to pay on the sale of your property and purchase.',
        buttons: [
          {
            theme: 'primary',
            text: 'Get started',
            url: '#calculator'
          }
        ]
      },

      calculator: {
        header: 'Be <strong class="font-extrabold">in the know</strong>',
        subHeader: 'Stamp duty is sometimes called land transfer duty and is a tax charged for property purchases by each Australian<br class="hidden lg:block"/> state and territory. Costs can vary from state to state.',
        disclaimer
      }
    },

    buyingCosts: {
      hero: {
        img: heroImg('buying-selling-hero'),
        header: `
              <strong class="font-extrabold">Buying Calculator</strong>
            `,
        subHeader: 'Use our calculator to work out an estimated cost of buying a property',
        buttons: [
          {
            theme: 'primary',
            text: 'Get started',
            url: '#calculator'
          }
        ]
      },

      calculator: {
        header: 'Buy <strong class="font-extrabold">with ease</strong>',
        subHeader: 'If you want to buy a new home, our buying calculator can assist you in knowing what associated costs are involved. Don’t be caught out.',
        disclaimer
      }
    },

    sellingCosts: {
      hero: {
        img: heroImg('buying-selling-hero'),
        header: `
              <strong class="font-extrabold">Selling Calculator</strong>
            `,
        subHeader: 'Use our calculator to work out an estimated cost of selling a property',
        buttons: [
          {
            theme: 'primary',
            text: 'Get started',
            url: '#calculator'
          }
        ]
      },

      calculator: {
        header: 'Sell <strong class="font-extrabold">with ease</strong>',
        subHeader: 'If you’re looking to sell your home, our selling calculator can assist you in knowing what associated costs are involved. Don’t be caught out.',
        disclaimer
      }
    },

    deposit: {
      hero: {
        img: heroImg('deposit-calculator-hero'),
        header: '<strong class="font-extrabold">Deposit calculator</strong>',
        subHeader: 'Thinking about purchasing a home and need a deposit? Use our<br class="hidden lg:block"/> home loans deposit calculator to see how much you will need.',
        buttons: [
          {
            theme: 'primary',
            text: 'Get started',
            url: '#calculator'
          }
        ]
      },

      calculator: {
        header: 'Learn how much <strong class="font-extrabold">you need</strong>',
        subHeader: 'Use our home loan deposit calculator to estimate how much you’ll have left for stamp duty<br class="hidden lg:block" /> and other upfront costs.',
        disclaimer
      }
    },

    budgetPlanner: {
      hero: {
        img: heroImg('budget-calculator-hero'),
        header: '<strong class="font-extrabold">Budget Planner<br/>calculator</strong>',
        subHeader: 'Want to know where you can save?<br class="hidden lg:block"/> Let our budget planner calculator help.',
        buttons: [
          {
            theme: 'primary',
            text: 'Get started',
            url: '#calculator'
          }
        ]
      },

      calculator: {
        header: 'Get a <strong class="font-extrabold">bird’s eye view of your budget</strong>',
        subHeader: 'Sick of not knowing where your money is going or want an idea of how much you can be saving a month?<br class="hidden lg:block"/> Our Budget Planner Calculator can help give you a picture of your finances.',
        disclaimer
      }
    }
  })
})

const pageData = computed(() => {
  return getKey(type, {
    refinance: {
      title: 'Home Loan Refinance Calculator | Compare Club',
      metaDescription: 'Use our Home Loan Refinance Calculator & be better prepared for your home loan. Click here.',
      canonical: 'https://compareclub.com.au/home-loans/calculators/refinance/'
    },
    borrowingPower: {
      title: '2024 Home Loan Borrowing Power Calculator | Compare Club',
      metaDescription: 'Use our Home Loan Borrowing Power Calculator & be better prepared for your home loan. Click here.',
      canonical: 'https://compareclub.com.au/home-loans/calculators/borrowing-power/'
    },
    stampDuty: {
      title: 'Stamp Duty Calculator | Compare Club',
      metaDescription: 'Use our Stamp Duty Calculator & be better prepared for your home loan. Click here.',
      canonical: 'https://compareclub.com.au/home-loans/calculators/stamp-duty/'
    },
    deposit: {
      title: 'Australia Home Loan Deposit Calculator | Compare Club',
      metaDescription: 'Use our Australia Home Loan Deposit Calculator & be better prepared for your home loan. Click here.',
      canonical: 'https://compareclub.com.au/home-loans/calculators/deposit/'
    },
    budgetPlanner: {
      title: '2023 Budget Planning Calculator | Compare Club',
      metaDescription: 'Use our Budget Planning Calculator & be better prepared for your home loan. Click here.',
      canonical: 'https://compareclub.com.au/home-loans/calculators/budget-planner/'
    },
    buyingCosts: {
      title: 'Home Buying Cost Calculator | Compare Club',
      metaDescription: 'Use our Home Buying Cost Calculator & be better prepared for your home loan. Click here.',
      canonical: 'https://compareclub.com.au/home-loans/calculators/buying-costs/'
    },
    sellingCosts: {
      title: 'Home Selling Cost Calculator | Compare Club',
      metaDescription: 'Use our Home Selling Cost Calculator & be better prepared for your home loan. Click here.',
      canonical: 'https://compareclub.com.au/home-loans/calculators/selling-costs/'
    }
  })
})

const homeLoanRates = await (async () => {
  try {
    const responses = await Promise.all([
      useAsyncData(() => service.getVariableRate()),
      useAsyncData(() => service.comparisonRateItem()),
      useAsyncData(() => service.cashRateDateItem())
    ])

    const data = {}

    responses.forEach(item => {
      const payload = item.data.value?.data[0]
      const tempKey = payload.fieldName !== 'cashRateDate'
        ? payload.fieldName
        : 'date'

      data[payload.fieldName] = payload.fieldValue || verticalData.cashRates?.[tempKey]
    })

    return data
  } catch {
    return {
      variableRate: verticalData.cashRates.variableRate,
      comparisonRate: verticalData.cashRates.comparisonRate,
      cashRateDate: verticalData.cashRates.date
    }
  }
})()

generateMeta({
  title: pageData.value.title,
  ogTitle: pageData.value.title,
  description: pageData.value.metaDescription,
  ogDescription: pageData.value.metaDescription,
  ogUrl: trailingSlash(`${CC_DOMAIN}${$route.path}`),
  ogImage: '',
  ogType: 'product',
  canonical: pageData.value.canonical
}, pageIsFunnel($route))

useJsonld(() => {
  const _path = trailingSlash($route.path)

  const {
    trustScore,
    numberOfReviews
  } = trustpilotStore.trustpilotData ?? {}

  return {
    '@context': 'https://schema.org',
    '@graph': [
      GENERATE_WEBPAGE_SCHEMA({
        page: _path,
        pageTitle: pageData.value.title,
        pageDescription: pageData.value.metaDescription,
        aboutPath: `${_path}#service`,
        mainEntityPath: `${_path}#service`,
        primaryImageOfPagePath: '/#logo',
        breadcrumbPath: `${_path}#breadcrumb`
      }),
      GENERATE_SERVICE_SCHEMA({
        page: _path,
        name: 'Home Loan Comparison',
        serviceType: 'Financial service',
        description: 'Our brokers help keep the cost of your monthly mortgage low. Our experts help hundreds of homeowners, new buyers, and investors find better value home loans each week.',
        audienceName: 'Australian consumers comparing home loan',
        servicePhone: '+611300757819',
        trustpilot: {
          rating: trustScore,
          count: numberOfReviews?.total
        }
      }),
      GENERATE_BREADCRUMB_SCHEMA(_path)
    ]
  }
})
</script>
